import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeft } from "lucide-react";
import { Button, Card, CardContent } from "@mui/material";
import { saveScriptToWatchlist, searchScript } from "../services/authService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import debounce from "lodash.debounce";
import MarketDetailDialog from "./MarketDetailDialog";
import {
  fetchMarketDataRequest,
  closeDialog,
  stopMarketDataStream,
  resetError
} from "../store/reducers/searchReducer";
import dayjs from 'dayjs';

function SearchBar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const {
    selectedItem,
    dialogOpen,
    loading: marketLoading,
    dialogLoading,
    isStreaming,
    lastUpdated
  } = useSelector(state => state.search);


  const handleGoBack = () => navigate(-1);

  const [userData, setUserData] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formattedDate, setFormattedDate] = useState(null);

  // Ref to prevent multiple clicks
  const clickTimeoutRef = useRef(null);
  const lastClickedItemRef = useRef(null);
  const searchInputRef = useRef(null);
 
  useEffect(() => {
    if (!dialogLoading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [dialogLoading]);
 
  const fetchSearchScript = async (query) => {
    if (!query) {
      setFilteredData([]);
      return;
    }
    setLoading(true);
    try {
      const response = await searchScript(query);
      const data = response.data || [];
      setFilteredData(data);
    } catch (error) {
      console.error("API Error:", error);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToWatchlist = async (item) => {
    if (!clients || !clients.client_id || !item.exchange_id || !item.script_id) {
      toast.error("Failed to add to watchlist.");
      return;
    }

    setLoading(true);
    try {
      const response = await saveScriptToWatchlist(
        clients.client_id,
        item.exchange_id,
        item.script_id
      );
      // API returns HTTP 200 even when validation fails (success: false),
      // e.g. when the script is already in the watchlist. Respect that flag.
      if (response && response.success === false) {
        toast.info(response.message || "Script already in watchlist.");
      } else {
        toast.success(response?.message || "Added to watchlist!");
      }
    } catch (error) {
      toast.error("Failed to add to watchlist.");
    } finally {
      setLoading(false);
    }
  };

  // Optimized script click handler with debouncing
  const handleScriptClick = useCallback((item) => {

    if (lastClickedItemRef.current?.token === item.token && dialogLoading) {
      return;
    }

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    lastClickedItemRef.current = item;

    dispatch(fetchMarketDataRequest({ item }));

  }, [dispatch, dialogLoading]);

  // Handle dialog close and cleanup
  const handleDialogClose = useCallback(() => {
    dispatch(stopMarketDataStream());
    dispatch(closeDialog());
    lastClickedItemRef.current = null;
  }, [dispatch]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      dispatch(stopMarketDataStream());
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, [dispatch]);

  const debouncedSearch = useCallback(
    debounce((query) => {
      fetchSearchScript(query);
    }, 400),
    []
  );

  useEffect(() => {
    const encodedData = localStorage.getItem('user_data');
    if (encodedData) {
      try {
        const decodedData = atob(encodedData);
        const userData = JSON.parse(decodedData);

        if (userData && userData.client && Array.isArray(userData.client)) {
          setClients(userData.client[0]);
          setUserData(userData);
        } else {
          console.log("No clients found or data is not in expected format.");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }

    if (searchQuery.length >= 3) {
      debouncedSearch(searchQuery);
    } else {
      setFilteredData([]);
    }

    return () => debouncedSearch.cancel();
  }, [searchQuery, debouncedSearch]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClear = () => {
    setSearchQuery("");
    setFilteredData([]);
  };

  const extractDateFromSymbol = (tradingSymbol) => {
    const match = tradingSymbol.match(/\d{1,2}[A-Z]{3}\d{2}/);
    return match ? match[0] : null;
  };

  useEffect(() => {
    if (selectedItem !== null) {
      let formattedDate = dayjs(extractDateFromSymbol(selectedItem.tradingSymbol)).format('YYYY-MM-DD');
      setFormattedDate(formattedDate);

    }
  }, [dispatch, selectedItem])


  const handleRedirect = (buttonType, tradingSymbol, exchange, price, lots, netChange, percentChange, symbolToken, lowerCircuit, upperCircuit, high, low, script_id, exchange_id) => {

    navigate('/add-order', {
      state: {
        m_buttonType: buttonType,
        m_scriptName: tradingSymbol,
        m_exchange: exchange,
        m_price: price,
        m_lotssize: 1,
        m_qty: lots,
        m_netChange: netChange,
        m_percentChange: percentChange,
        m_symbolToken: symbolToken,
        m_activeTabValue: null,
        m_expiry: formattedDate,
        m_lowerCircuit: lowerCircuit,
        m_upperCircuit: upperCircuit,
        m_high: high,
        m_low: low,
        m_script_id: script_id,
        m_exchange_id: exchange_id
      },
    });

    handleDialogClose();
  };
  return (
    <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
      <div className="h-[100vh] overflow-y-scroll p-4 flex-grow max-w-sm min-w-[370px] w-full bg-gray-100
            [&::-webkit-scrollbar]:w-1 
            [&::-webkit-scrollbar-track]:bg-transparent 
            [&::-webkit-scrollbar-thumb]:bg-transparent 
            scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent">

        {/* Header with Back Button */}
        <p className="text-[1rem] leading-6 mb-[30px] mt-0 tracking-wide font-lato font-semibold flex items-center">
          <span className="font-black me-2">
            <ChevronLeft size={16} onClick={handleGoBack} />
          </span>
          Add Script
          {/* Show streaming indicator */}
          {isStreaming && (
            <span className="ml-2 text-green-500 text-xs">
              ● Live
            </span>
          )}
        </p>

        {/* Search Input with Clear Button */}
        <div className="mb-4 relative">
          <input
            ref={searchInputRef}
            placeholder="Search scripts..."
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            className="w-full bg-white rounded-[5px] text-[12px] leading-[18px] py-[0.688rem] pr-[3rem] pl-[2.2rem] border border-gray-300"
            disabled={dialogLoading}
          />
          {searchQuery && (
            <button
              onClick={handleClear}
              className="absolute top-[50%] right-[10px] transform -translate-y-[50%] text-[12px] font-semibold cursor-pointer text-blue-500"
              disabled={dialogLoading}
            >
              Clear
            </button>
          )}
        </div>

        {/* Display Loading Indicator */}
        {(loading || (marketLoading && !dialogOpen)) && (
          <p className="text-gray-500 text-center">Loading...</p>
        )}

        {/* Dialog Loading Indicator */}
        {dialogLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span className="text-sm">Loading market data...</span>
            </div>
          </div>
        )}

        {/* Display Search Results in MUI Cards */}
        <div className="mt-4 space-y-2">
          {filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <Card
                key={`${item.token}-${index}`}
                className={`rounded-lg shadow-md bg-white cursor-pointer hover:shadow-lg transition-all ${dialogLoading && lastClickedItemRef.current?.token === item.token
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
                  }`}
                onClick={() => !dialogLoading && handleScriptClick(item)}
              >
                <CardContent className="flex justify-between items-center">
                  <div className="flex justify-between items-center w-full h-[20px] mt-[10px]">
                    {/* Name and Date Section */}
                    <div className="flex flex-col items-start">
                      <span className="font-semibold text-[14px] py-1">
                        {item.symbol}
                        {/* Show loading indicator for clicked item */}
                        {dialogLoading && lastClickedItemRef.current?.token === item.token && (
                          <span className="ml-2 text-xs text-blue-500">Loading...</span>
                        )}
                      </span>

                      <span className="font-semibold text-gray-400 text-[12px] py-1">
                        {item.script_name}
                        <span
                          className={`font-lato text-[12px] text-black p-1 font-medium uppercase rounded-sm ml-1 
                            ${item.exchange_name === 'EQUITY' ? 'bg-sky-100' :
                              item.exchange_name === 'EQUITY(INDEX)' ? 'bg-orange-100' :
                                item.exchange_name === 'MCX' ? 'bg-red-100' :
                                  item.exchange_name === 'CDS' ? 'bg-green-100' :
                                    'bg-gray-100'}`}
                        >
                          {item.exchange_name}
                        </span>
                        <span className="text-[14px] text-gray-400 ml-2">
                          Lots Size: {item.lot_size}
                        </span>
                      </span>
                    </div>

                    {/* Add to Watchlist Button */}
                    <div className="flex flex-col items-end">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveToWatchlist(item);
                        }}
                        className="text-[30px] text-blue-500 -mr-[10px]"
                        disabled={loading || dialogLoading}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            searchQuery &&
            !loading && (
              <p className="text-gray-500 text-center mt-4">
                No results found for "{searchQuery}"
              </p>
            )
          )}
        </div>

        {/* Last Updated Info */}
        {lastUpdated && isStreaming && (
          <div className="fixed bottom-4 right-4 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
            Last updated: {new Date(lastUpdated).toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Market Detail Dialog */}
      <MarketDetailDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        selectedItem={selectedItem}
        formattedDate={formattedDate}
        handleRedirect={handleRedirect}
        isStreaming={isStreaming}
        lastUpdated={lastUpdated}
      />

      <ToastContainer />
    </div>
  );
}

export default SearchBar;